'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      question: 'Who should request an initial conversation?',
      answer: 'We work with senior decision-makers in government ministries, state-owned enterprises, regulatory authorities, and development organizations who have mandate and authority to pursue institutional transformation initiatives. Ideal contacts include ministers, permanent secretaries, CEOs, directors of strategy, and program directors responsible for organizational change.',
    },
    {
      question: 'What happens after I submit a conversation request?',
      answer: 'Our senior advisory team reviews all requests within 2-3 business days. If there appears to be strong alignment between your institutional needs and our capabilities, we will contact you to schedule an initial exploratory discussion. This conversation is confidential, substantive, and designed to assess mutual fit without obligation.',
    },
    {
      question: 'How long does the initial conversation typically last?',
      answer: 'Initial conversations usually last 45-60 minutes. This allows sufficient time to understand your strategic context, discuss your transformation challenges, explain our systematic approach, and determine whether there is potential for a productive long-term partnership.',
    },
    {
      question: 'What if our organization is not ready for immediate engagement?',
      answer: 'We welcome exploratory conversations even if you are in early planning stages. Understanding your timeline and constraints helps us provide relevant guidance and determine the right moment for potential collaboration. There is no pressure for immediate commitment.',
    },
    {
      question: 'Do you work with organizations outside government and public sector?',
      answer: 'While our primary focus is government, state-owned enterprises, and development organizations, we occasionally work with private sector institutions facing similar strategic and organizational transformation challenges. The key criterion is commitment to long-term institutional capacity building.',
    },
    {
      question: 'What makes Diaax different from large consulting firms?',
      answer: 'Diaax is a senior advisory practice providing direct access to experienced strategic advisors through a focused engagement model. We integrate strategy development with leadership capacity building, focusing on sustainable institutional transformation rather than report delivery. Our selective approach ensures dedicated attention and long-term partnership quality.',
    },
    {
      question: 'How do you ensure confidentiality of our discussions?',
      answer: 'All communications are handled with strict professional confidentiality. We understand the sensitive nature of institutional transformation discussions and maintain appropriate discretion for government and public sector engagements. Non-disclosure agreements can be established before detailed discussions.',
    },
    {
      question: 'What is your typical engagement timeline?',
      answer: 'Our flagship integrated programs typically run 6-12 months, combining strategic planning with leadership development and execution support. However, engagement scope and timeline are customized based on your institutional needs, constraints, and transformation objectives. We discuss timeline considerations during initial conversations.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <div className="bg-white rounded-lg institutional-shadow p-5 sm:p-8 lg:p-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="h-16 bg-muted rounded"></div>
          <div className="h-16 bg-muted rounded"></div>
          <div className="h-16 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg institutional-shadow p-5 sm:p-8 lg:p-12">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-text-primary mb-3 sm:mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-sm sm:text-base text-text-secondary leading-relaxed">
          Common questions about requesting an initial conversation and working with Diaax Advisory.
        </p>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-border rounded-lg overflow-hidden transition-institutional"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-4 text-left hover:bg-surface transition-fast"
            >
              <span className="font-semibold text-text-primary text-sm sm:text-base">{faq.question}</span>
              <Icon
                name="ChevronDownIcon"
                size={18}
                className={`text-institutional flex-shrink-0 transition-transform ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
              />
            </button>
            {openIndex === index && (
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-surface border-t border-border">
                <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 sm:mt-8 bg-institutional/5 rounded-lg p-4 sm:p-6 institutional-border">
        <div className="flex items-start gap-3 sm:gap-4">
          <Icon name="QuestionMarkCircleIcon" size={22} className="text-institutional flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-text-primary mb-2 text-sm sm:text-base">
              Have a Different Question?
            </h3>
            <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">
              If your question is not addressed here, please include it in the additional information section of the conversation request form, or contact us directly at info@diaax.com.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;